import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

const AuthContext = createContext(null);

// API-Basis-URL — identisch mit appConfig.apiBaseUrl in config.js.
// Wird lazy geladen damit AuthContext keinen zyklischen Import erzeugt.
let _apiBase = null;
async function getApiBase() {
  if (_apiBase) return _apiBase;
  try {
    const { appConfig } = await import('../config');
    _apiBase = appConfig.apiBaseUrl || 'https://app.hejvi.de/api';
  } catch {
    _apiBase = 'https://app.hejvi.de/api';
  }
  return _apiBase;
}

const LS_TOKEN   = 'hejvi_access_token';
const LS_REFRESH = 'hejvi_refresh_token';
const LS_USER    = 'hejvi_user';
const LS_FEAT    = 'hejvi_features';
const LS_EXPIRY  = 'hejvi_token_expiry';

// Standardmäßig alle Features false
const DEFAULT_FEATURES = {
  // Learning
  courses: false, challenges: false, favourites: false, achievements: false,
  download_document: false, leaderboard: false,
  // Generative
  video_builder: false, element_creator: false, tts: false,
  quiz_builder: false, course_builder: false,
  // Intelligence
  frag_hejvi: false, document_chat: false,
  domain_knowledge: false, company_knowledge: false,
  // Admin (group-gesteuert)
  admin_panel: false, user_management: false, group_management: false,
  content_management: false, content_approval: false, analytics: false,
  knowledge_base: false, super_admin: false,
  // Admin-Tab-Freigaben (group 0/2/3 via admin_role_config + plan, vom Backend gesetzt)
  admin_user_management: false,
  admin_group_management: false,
  admin_content_management: false,
  admin_content_approval: false,
  admin_analytics: false,
  admin_knowledge_base: false,
  admin_frag_hejvi: false,
  admin_document_chat: false,
  admin_domain_knowledge: false,
  admin_notifications: false,
  admin_company_knowledge: false,
};

function parseFeatures(raw) {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_FEATURES };
  const flat = { ...DEFAULT_FEATURES };
  const merge = (obj) => {
    if (!obj || typeof obj !== 'object') return;
    Object.entries(obj).forEach(([k, v]) => {
      if (typeof v === 'boolean') {
        flat[k] = v;
      } else if (typeof v === 'object') {
        merge(v);
      }
    });
  };
  merge(raw);
  return flat;
}

function readStoredFeatures() {
  try {
    const raw = localStorage.getItem(LS_FEAT);
    if (!raw) return { ...DEFAULT_FEATURES };
    return parseFeatures(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_FEATURES };
  }
}

function readStoredUser() {
  try {
    const raw = localStorage.getItem(LS_USER);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function AuthProvider({ children }) {
  const [token, setToken]         = useState(() => localStorage.getItem(LS_TOKEN) || null);
  const [user, setUser]           = useState(readStoredUser);
  const [features, setFeatures]   = useState(readStoredFeatures);
  const [uiLanguages, setUiLanguages] = useState([]);

  // Promise-basierte Refresh-Deduplizierung:
  // null    = kein Refresh läuft
  // Promise = Refresh läuft — concurrent Caller awaiten dieses Promise statt
  //           null zu bekommen (verhindert spurious-logout durch Race Condition).
  //
  // Bug den das behebt:
  //   1. visibilitychange → doRefresh() startet, setzt Promise
  //   2. Notification-Poll bekommt 401 → ruft doRefresh() auf
  //   3. Alt (boolean lock): gibt null zurück → makeAuthenticatedRequest → logout()
  //   4. Neu (promise dedup): awaitet laufenden Refresh → bekommt neuen Token → kein Logout
  const refreshPromiseRef = useRef(null);

  // Verhindert, dass der /user-Fetch durch doRefresh-ausgelöste Token-State-Änderungen
  // mehrfach gleichzeitig feuert. Wird beim ersten erfolgreichen Fetch auf true gesetzt
  // und bei logout() zurückgesetzt.
  const userFetchedRef = useRef(false);

  // Ref auf logout() — damit makeAuthenticatedRequest logout aufrufen kann,
  // ohne zirkuläre useCallback-Abhängigkeiten zu erzeugen.
  const logoutRef = useRef(null);

  // ───────────────────────────────────────────────
  // Token-Refresh — Promise-dedupliziert
  // ───────────────────────────────────────────────
  const doRefresh = useCallback(async () => {
    // Wenn bereits ein Refresh in flight ist: drauf warten und neuen Token aus
    // localStorage lesen. Verhindert den spurious-logout-Bug bei concurrent Callers.
    if (refreshPromiseRef.current) {
      try { await refreshPromiseRef.current; } catch { /* ignore */ }
      return localStorage.getItem(LS_TOKEN);
    }

    const refreshToken = localStorage.getItem(LS_REFRESH);
    if (!refreshToken) return null;

    // IIFE speichern damit concurrent Caller es awaiten können.
    // Promise resolved immer (niemals reject) — Aufrufer bekommt newToken oder null.
    const promise = (async () => {
      try {
        const apiBase = await getApiBase();
        const res = await fetch(`${apiBase}/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${refreshToken}`,
          },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
        if (!res.ok) throw new Error('refresh failed');
        const data = await res.json();
        const newToken  = data.access_token || data.token;
        const newExpiry = data.expires_at || (Date.now() / 1000 + (data.expires_in || 3600));
        localStorage.setItem(LS_TOKEN,  newToken);
        localStorage.setItem(LS_EXPIRY, String(newExpiry));
        if (data.refresh_token) localStorage.setItem(LS_REFRESH, data.refresh_token);
        // Features aus Refresh-Response sofort übernehmen (Plan-/Group-Änderungen ohne Re-Login)
        if (data.features) {
          const flat = parseFeatures(data.features);
          localStorage.setItem(LS_FEAT, JSON.stringify(flat));
          setFeatures(flat);
        }
        // SSE-Verbindungen (useNotifications) über neuen Token informieren → sofort reconnecten
        window.dispatchEvent(new CustomEvent('hejvi:token-refreshed', { detail: { token: newToken } }));
        return newToken;
      } catch {
        return null;
      } finally {
        refreshPromiseRef.current = null;
      }
    })();

    refreshPromiseRef.current = promise;
    return promise;
  }, []);

  // Proaktiver Pre-Expiry-Refresh (60 Sek. vor Ablauf)
  const getValidToken = useCallback(async () => {
    const expiry = parseFloat(localStorage.getItem(LS_EXPIRY) || '0');
    const now = Date.now() / 1000;
    if (expiry && expiry - now < 60) {
      const refreshed = await doRefresh();
      return refreshed || localStorage.getItem(LS_TOKEN);
    }
    return localStorage.getItem(LS_TOKEN);
  }, [doRefresh]);

  // ───────────────────────────────────────────────
  // makeAuthenticatedRequest — gibt pre-parsed JSON zurück
  // WICHTIG: Aufrufer darf KEIN .json() mehr aufrufen
  // ───────────────────────────────────────────────
  const makeAuthenticatedRequest = useCallback(async (url, options = {}) => {
    let currentToken = await getValidToken();

    // Relative URLs (z.B. '/user') mit API-Basis prefixen.
    // Absolute URLs (http/https) werden unverändert verwendet.
    const apiBase = await getApiBase();
    const fullUrl = url.startsWith('http') ? url : `${apiBase}${url}`;

    const doFetch = async (tkn) => {
      // Content-Type nur setzen wenn kein FormData-Body — sonst setzt der Browser
      // automatisch multipart/form-data mit korrekter boundary.
      const isFormData = options.body instanceof FormData;
      const headers = {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(options.headers || {}),
        ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}),
      };
      return fetch(fullUrl, { ...options, headers });
    };

    let res = await doFetch(currentToken);

    // 401 → Token-Refresh versuchen
    if (res.status === 401) {
      const refreshed = await doRefresh();
      if (refreshed) {
        currentToken = refreshed;
        res = await doFetch(currentToken);
      } else {
        // Refresh fehlgeschlagen (Token + Refresh-Token abgelaufen) → Session beenden.
        logoutRef.current?.();
        const err = new Error('Unauthorized');
        err.status = 401;
        throw err;
      }
    }

    // Antwort als JSON parsen und zurückgeben
    let data;
    const contentType = res.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      data = await res.json();
    } else {
      const text = await res.text();
      try { data = JSON.parse(text); } catch { data = { message: text }; }
    }

    if (!res.ok) {
      const err = new Error(data?.error || data?.message || `HTTP ${res.status}`);
      err.status = res.status;
      err.data = data;
      throw err;
    }

    // Kompatibilitäts-Shim: Einige ältere Komponenten rufen noch .json() auf dem
    // Rückgabewert auf. Der Shim fängt das ab und gibt data unverändert zurück.
    if (data && typeof data === 'object' && !data.json) {
      data.json = () => Promise.resolve(data);
      data.ok   = true;
    }
    return data;
  }, [getValidToken, doRefresh]);

  // ───────────────────────────────────────────────
  // /user fetch beim Mount — holt aktuellen Nutzer + Features
  // ───────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    if (userFetchedRef.current) return;
    userFetchedRef.current = true;

    const storedUser = readStoredUser();
    if (storedUser) setUser(storedUser);

    (async () => {
      try {
        const data = await makeAuthenticatedRequest('/user');
        if (data?.user) {
          setUser(data.user);
          localStorage.setItem(LS_USER, JSON.stringify(data.user));
        }
        if (data?.features) {
          const flat = parseFeatures(data.features);
          setFeatures(flat);
          localStorage.setItem(LS_FEAT, JSON.stringify(flat));
        }
        if (data?.ui_languages) {
          setUiLanguages(data.ui_languages);
        }
      } catch (e) {
        if (e.status === 401) {
          userFetchedRef.current = false;
          logout();
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ───────────────────────────────────────────────
  // hejvi:features-updated — externes Features-Update sofort übernehmen
  // ───────────────────────────────────────────────
  useEffect(() => {
    const onFeaturesUpdated = (e) => {
      if (!e.detail || typeof e.detail !== 'object') return;
      const flat = parseFeatures(e.detail);
      localStorage.setItem(LS_FEAT, JSON.stringify(flat));
      setFeatures(flat);
    };
    window.addEventListener('hejvi:features-updated', onFeaturesUpdated);
    return () => window.removeEventListener('hejvi:features-updated', onFeaturesUpdated);
  }, []);

  // ───────────────────────────────────────────────
  // Login — speichert Token + User + Features
  // ───────────────────────────────────────────────
  const login = useCallback((response) => {
    const accessToken  = response.access_token || response.token;
    const refreshToken = response.refresh_token;
    const expiry       = response.expires_at || (Date.now() / 1000 + (response.expires_in || 3600));
    const userData     = response.user;
    const featData     = response.features;

    if (accessToken) {
      localStorage.setItem(LS_TOKEN,  accessToken);
      localStorage.setItem(LS_EXPIRY, String(expiry));
      setToken(accessToken);
    }
    if (refreshToken) {
      localStorage.setItem(LS_REFRESH, refreshToken);
    }
    if (userData) {
      localStorage.setItem(LS_USER, JSON.stringify(userData));
      setUser(userData);
    }
    if (featData) {
      const flat = parseFeatures(featData);
      localStorage.setItem(LS_FEAT, JSON.stringify(flat));
      setFeatures(flat);
    }
    if (response.ui_languages) {
      setUiLanguages(response.ui_languages);
    }
  }, []);

  // ───────────────────────────────────────────────
  // Logout
  // ───────────────────────────────────────────────
  const logout = useCallback(() => {
    localStorage.removeItem(LS_TOKEN);
    localStorage.removeItem(LS_REFRESH);
    localStorage.removeItem(LS_USER);
    localStorage.removeItem(LS_FEAT);
    localStorage.removeItem(LS_EXPIRY);
    userFetchedRef.current = false;
    setToken(null);
    setUser(null);
    setFeatures({ ...DEFAULT_FEATURES });
    setUiLanguages([]);
  }, []);

  // logoutRef immer aktuell halten — für makeAuthenticatedRequest
  useEffect(() => { logoutRef.current = logout; }, [logout]);

  // ───────────────────────────────────────────────
  // Cross-Tab Logout-Sync
  //
  // Der storage-Event feuert in ANDEREN Tabs wenn Tab A localStorage schreibt.
  // Wenn Tab A ausloggt (LS_TOKEN wird gelöscht), bekommen alle anderen Tabs
  // dieses Event und loggen sich ebenfalls aus → Redirect zu /login.
  // ───────────────────────────────────────────────
  useEffect(() => {
    const handleStorage = (e) => {
      // Nur reagieren wenn das Access-Token in einem anderen Tab entfernt wurde
      if (e.key !== LS_TOKEN) return;
      if (e.newValue !== null) return;           // Token wurde gesetzt, nicht gelöscht
      if (localStorage.getItem(LS_TOKEN)) return; // Sicherheit: Token noch vorhanden

      // Anderer Tab hat ausgeloggt → auch in diesem Tab ausloggen
      logoutRef.current?.();
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []); // logoutRef ist stabil

  // ───────────────────────────────────────────────
  // Visibility-Check — "Banking-App"-Verhalten
  //
  // Fenster auf 300s erweitert (von 60s) — konsistent mit proaktivem Timer.
  // Da doRefresh() promise-dedupliziert ist, können visibility-Handler und
  // concurrent 401-Handler beide doRefresh() aufrufen ohne dass einer null
  // bekommt und einen spurious Logout auslöst.
  // ───────────────────────────────────────────────
  useEffect(() => {
    const handleVisibility = async () => {
      if (document.visibilityState !== 'visible') return;
      const storedToken = localStorage.getItem(LS_TOKEN);
      if (!storedToken) return;

      const expiry = parseFloat(localStorage.getItem(LS_EXPIRY) || '0');
      const now    = Date.now() / 1000;

      // Token abgelaufen oder läuft in weniger als 5 Min ab → Refresh versuchen
      if (expiry && expiry - now < 300) {
        const refreshed = await doRefresh();
        if (!refreshed) {
          // Nur logout wenn Token schon länger als 5 Min abgelaufen ist.
          // Bei kürzlich abgelaufenen Tokens (z.B. nach langem Import) gibt es
          // oft kurze Netzwerkunterbrüche — kein sofortiger Logout.
          if (expiry - now < -300) {
            logout();
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [doRefresh, logout]);

  // ───────────────────────────────────────────────
  // Silent-Refresh-Timer — läuft alle 60 Sekunden.
  //
  // Fenster auf 600s (10 Min) erweitert — Puffer gegen Browser-Timer-Throttling
  // im Hintergrund (Chrome drosselt Intervals auf ≥1 Min, manchmal mehr).
  // Bei 10-Min-Fenster und 60s-Interval sind bis zu 9 Versuche möglich bevor
  // das Token abläuft — selbst bei aggressivem Browser-Throttling.
  // ───────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(async () => {
      const expiry = parseFloat(localStorage.getItem(LS_EXPIRY) || '0');
      const now    = Date.now() / 1000;
      // < 600s bis Ablauf ODER bereits abgelaufen (kein "> 0"-Check):
      // Wenn der Browser-Tab gedrosselt wird und der Timer zu spät feuert,
      // muss auch ein bereits abgelaufenes Token refresht werden können.
      if (expiry && expiry - now < 600) {
        await doRefresh();
      }
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, [token, doRefresh]);

  // ───────────────────────────────────────────────
  // hasFeature(key) — boolean Helper
  // Unterstützt dot-notation: hasFeature('generative.video_builder')
  // ───────────────────────────────────────────────
  const hasFeature = useCallback((key) => {
    if (!key) return false;
    if (key.includes('.')) {
      const parts = key.split('.');
      return !!features[parts[parts.length - 1]];
    }
    return !!features[key];
  }, [features]);

  // ───────────────────────────────────────────────
  // hasAdminTab(key) — prüft ob ein Admin-Tab sichtbar sein soll
  // ───────────────────────────────────────────────
  const hasAdminTab = useCallback((key) => {
    if (!key) return false;
    return !!features['admin_' + key] || !!features[key];
  }, [features]);

  // ───────────────────────────────────────────────
  // API-Convenience-Funktionen
  // ───────────────────────────────────────────────

  const getUser = useCallback(async () => {
    return makeAuthenticatedRequest('/user');
  }, [makeAuthenticatedRequest]);

  const getFeed = useCallback(async (params = {}) => {
    const q = new URLSearchParams();
    if (params.page)             q.set('page',            String(params.page));
    if (params.tags?.length)     q.set('tags',            params.tags.join(','));
    if (params.mandatory)        q.set('mandatory',       String(params.mandatory));
    if (params.favourites_only)  q.set('favourites_only', String(params.favourites_only));
    const qs = q.toString();
    return makeAuthenticatedRequest(qs ? `/feed?${qs}` : '/feed');
  }, [makeAuthenticatedRequest]);

  const toggleFavourites = useCallback(async (elementId) => {
    return makeAuthenticatedRequest('/toggle-favourites-post', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ hash_id: elementId }),
    });
  }, [makeAuthenticatedRequest]);

  const setProgress = useCallback(async (elementId, progress) => {
    return makeAuthenticatedRequest('/set-progress', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ element_id: elementId, progress }),
    });
  }, [makeAuthenticatedRequest]);

  const searchByTerm = useCallback(async (term, params = {}) => {
    const q = new URLSearchParams({ term, ...Object.fromEntries(
      Object.entries(params).map(([k, v]) => [k, String(v)])
    )});
    return makeAuthenticatedRequest(`/byterm?${q.toString()}`);
  }, [makeAuthenticatedRequest]);

  const getMyCourses = useCallback(async () => {
    return makeAuthenticatedRequest('/mycourses');
  }, [makeAuthenticatedRequest]);

  const getFavourites = useCallback(async () => {
    return makeAuthenticatedRequest('/favourites');
  }, [makeAuthenticatedRequest]);

  const getChallenges = useCallback(async (params = {}) => {
    const q = new URLSearchParams();
    if (params.tags?.length) q.set('tags', params.tags.join(','));
    const qs = q.toString();
    return makeAuthenticatedRequest(qs ? `/challenges?${qs}` : '/challenges');
  }, [makeAuthenticatedRequest]);

  const getCurrentChallenges = useCallback(async () => {
    return makeAuthenticatedRequest('/current-challenges');
  }, [makeAuthenticatedRequest]);

  const getElement = useCallback(async (hashId) => {
    return makeAuthenticatedRequest(`/element/${hashId}`);
  }, [makeAuthenticatedRequest]);

  const setSettings = useCallback((updater) => {
    setUser(prev => {
      const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      localStorage.setItem(LS_USER, JSON.stringify(next));
      return next;
    });
  }, []);

  const value = {
    // Auth-State
    token,
    user,
    features,
    uiLanguages,
    // Methoden
    login,
    logout,
    hasFeature,
    hasAdminTab,
    makeAuthenticatedRequest,
    getValidToken,
    setSettings,
    // API-Convenience
    getUser,
    getFeed,
    getMyCourses,
    getFavourites,
    getChallenges,
    getCurrentChallenges,
    toggleFavourites,
    getElement,
    setProgress,
    searchByTerm,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth muss innerhalb von AuthProvider verwendet werden');
  return ctx;
}

// Named Exports für direkten Zugriff (Kompatibilität)
export default AuthContext;
